import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from '../lib/cx';
import { Icon } from './Icon';

const CONTROL =
  'w-full min-h-11 rounded-lg border bg-surface px-3.5 py-2 text-base text-fg shadow-[0_1px_1px_rgb(0_0_0/0.03)] placeholder:text-muted/65 ' +
  'border-border-strong/75 transition-colors focus:border-brand focus:outline-none focus:ring-3 focus:ring-ring/25 disabled:bg-surface-2 disabled:text-muted';

export interface FieldProps {
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Rendered after the label (e.g. a help button). */
  labelAction?: ReactNode;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}

/** Label + control + help + error, with ids wired for screen readers. */
export function Field({ label, help, error, required, labelAction, children, className }: FieldProps) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [helpId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      {label && (
        <div className="flex items-start justify-between gap-2">
          <label htmlFor={id} className="text-sm font-semibold text-fg">
            {label}
            {required && (
              <span className="ms-0.5 text-danger" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {labelAction}
        </div>
      )}
      {children(id, describedBy)}
      {help && (
        <p id={helpId} className="text-sm text-muted">
          {help}
        </p>
      )}
      {error && (
        <p id={errId} className="flex items-center gap-1.5 text-sm font-medium text-danger" role="alert">
          <Icon name="alertCircle" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} aria-invalid={invalid || undefined} className={cx(CONTROL, invalid && 'border-danger', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { className, invalid, rows = 3, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} aria-invalid={invalid || undefined} className={cx(CONTROL, 'resize-y', invalid && 'border-danger', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <div className="relative">
      <select ref={ref} aria-invalid={invalid || undefined} className={cx(CONTROL, 'appearance-none pe-10', invalid && 'border-danger', className)} {...rest}>
        {children}
      </select>
      <Icon name="chevronDown" size={18} className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
    </div>
  );
});

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  name,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  name?: string;
}) {
  return (
    <label
      className={cx(
        'flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors',
        checked ? 'border-brand bg-brand-soft' : 'border-border bg-surface hover:bg-surface-2',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} name={name} onChange={(e) => onChange(e.target.checked)} />
      <span
        aria-hidden="true"
        className={cx(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border-2 peer-focus-visible:ring-4 peer-focus-visible:ring-ring/40',
          checked ? 'border-brand bg-brand text-brand-contrast' : 'border-border bg-surface',
        )}
      >
        {checked && <Icon name="check" size={16} strokeWidth={3} />}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="font-medium">{label}</span>
        {description && <span className="text-sm text-muted">{description}</span>}
      </span>
    </label>
  );
}

export function RadioList<T extends string>({
  value,
  onChange,
  options,
  name,
  disabled,
}: {
  value: T | null | undefined;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; description?: ReactNode }[];
  name: string;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {options.map((o) => {
        const checked = value === o.value;
        return (
          <label
            key={o.value}
            className={cx(
              'flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5',
              checked ? 'border-brand bg-brand-soft' : 'border-border bg-surface hover:bg-surface-2',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input type="radio" className="peer sr-only" name={name} checked={checked} disabled={disabled} onChange={() => onChange(o.value)} />
            <span
              aria-hidden="true"
              className={cx('flex size-6 shrink-0 items-center justify-center rounded-full border-2', checked ? 'border-brand' : 'border-border')}
            >
              {checked && <span className="size-3 rounded-full bg-brand" />}
            </span>
            <span className="flex flex-col">
              <span className="font-medium">{o.label}</span>
              {o.description && <span className="text-sm text-muted">{o.description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Colour when selected. */
  tone?: 'brand' | 'success' | 'danger' | 'neutral' | 'warning';
}

const SEG_TONE = {
  brand: 'bg-brand text-brand-contrast border-brand shadow-xs',
  success: 'bg-success text-white border-success shadow-xs',
  danger: 'bg-danger text-white border-danger shadow-xs',
  warning: 'bg-warning text-white border-warning shadow-xs',
  neutral: 'bg-fg text-bg border-fg shadow-xs',
};

/** Large segmented buttons — the Yes / No / N/A control, button-style choices. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  disabled,
  size = 'lg',
  allowDeselect,
}: {
  value: T | null | undefined;
  onChange: (v: T | null) => void;
  options: SegmentOption<T>[];
  label: string;
  disabled?: boolean;
  size?: 'md' | 'lg';
  allowDeselect?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid gap-1.5 rounded-xl bg-surface-2 p-1 ring-1 ring-inset ring-border"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(selected && allowDeselect ? null : o.value)}
            className={cx(
              'rounded-lg border px-2 font-display font-bold tracking-tight transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50',
              size === 'lg' ? 'min-h-12 text-base' : 'min-h-10 text-sm',
              selected ? SEG_TONE[o.tone ?? 'brand'] : 'border-transparent bg-surface/75 text-fg hover:bg-surface',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pill-style multi/single select. */
export function Chips<T extends string>({
  options,
  selected,
  onToggle,
  disabled,
}: {
  options: { value: T; label: ReactNode }[];
  selected: T[];
  onToggle: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(o.value)}
            className={cx(
              'inline-flex min-h-10 items-center gap-1.5 rounded-lg border px-3.5 text-sm font-medium transition-colors disabled:opacity-50',
              on ? 'border-brand bg-brand text-brand-contrast shadow-2xs' : 'border-border-strong/70 bg-surface hover:bg-surface-2',
            )}
          >
            {on && <Icon name="check" size={15} strokeWidth={2.5} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
